import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Dispatch, useMemo } from 'react';
import { useState } from 'react';
import { SetStateAction } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'use-lodash-debounce';

import useAxiosHook from '../../../../hooks/useAxiosHook';
import { useEndpointGuard } from '../../../../hooks/useEndpointGuard';
import useHaveValuesChanged from '../../../../hooks/useHaveValuesChanged';
import useSearchQueryDropdownParam from '../../../../hooks/useSearchQueryDropdownParam';
import useSearchQueryParam from '../../../../hooks/useSearchQueryParam';
import { WithData } from '../../../../types/api';
import { MunicipalityCollection } from '../../../../types/api/municipalities';
import * as warehousesGuards from '../../../../utils/constants/auth/warehouses';
import { debounceTimeout } from '../../../../utils/constants/misc';
import { getSearchQueryParam } from '../../../../utils/helpers/searchQuery';
import FiltersCounter from '../../Components/Filters/FiltersCounter';

function useTableFilters(
  page: number,
  setPage: Dispatch<SetStateAction<number>>,
  limit: number
) {
  const { t } = useTranslation();
  const municipalityFilterGuard = useEndpointGuard(
    warehousesGuards.municipalityFilter
  );
  const placesFilterGuard = useEndpointGuard(warehousesGuards.placesFilter);
  const location = useLocation();
  const [mestoId, setMestoId] = useState<string>(
    () => getSearchQueryParam(location.search, 'mesto_id') ?? ''
  );
  const [opstinaId, setOpstinaId] = useState<string>(
    () => getSearchQueryParam(location.search, 'opstina_id') ?? ''
  );
  const [nameFilter, setNameFilter] = useState(
    () => getSearchQueryParam(location.search, 'name') ?? ''
  );

  const debouncedNameFilter = useDebounce(nameFilter, debounceTimeout);

  const { data: municipalities, isLoading: isMunicipalitiesLoading } =
    useAxiosHook<WithData<MunicipalityCollection>>('/municipalities?limit=0');

  const municipalityFilterOptions = useMemo(
    () =>
      municipalities?.data?.map((municipality) => ({
        label: municipality.name,
        value: municipality.id,
      })) ?? [],
    [municipalities]
  );

  const { data: places, isLoading: isPlacesLoading } = useAxiosHook<any>(
    '/places/municipalities',
    {
      skipWhen: !placesFilterGuard,
      skipCache: true,
    }
  );

  const placeFilterOptions = useMemo(
    () =>
      places?.map((place: any) => ({
        label: place.ime,
        value: place.id,
      })) ?? [],
    [places]
  );

  const filtersArr = useMemo(
    () => [{ nameFilter, mestoId, opstinaId }],
    [nameFilter, mestoId, opstinaId]
  );

  const haveFiltersChanged = useHaveValuesChanged(filtersArr);

  useEffect(() => {
    setPage(1);
  }, [nameFilter, opstinaId, mestoId, setPage]);

  useSearchQueryParam('name', debouncedNameFilter);

  useSearchQueryDropdownParam(
    'mesto_id',
    mestoId,
    setMestoId,
    placeFilterOptions
  );

  useSearchQueryDropdownParam(
    'opstina_id',
    opstinaId,
    setOpstinaId,
    municipalityFilterOptions
  );

  const httpFiltersObj = useMemo(
    () => ({
      name: debouncedNameFilter,
      placeId: mestoId,
      municipalityId: opstinaId,
      page: haveFiltersChanged ? 1 : page,
      limit,
    }),
    [debouncedNameFilter, mestoId, opstinaId, haveFiltersChanged, page, limit]
  );

  const basicFiltersActiveFilterCount = Object.values({
    nameFilter,
    mestoId,
    opstinaId,
  }).filter(Boolean).length;

  const filters = useMemo<JSX.Element>(
    () => (
      <Accordion multiple activeIndex={[0]}>
        <AccordionTab
          disabled
          headerTemplate={
            <FiltersCounter
              description={t('Basic filters')}
              counter={basicFiltersActiveFilterCount}
            />
          }
        >
          <div className="sidebar_filter">
            <label htmlFor="filter_name">{t('Name')}</label>
            <InputText
              id="filter_name"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
          {municipalityFilterGuard && (
            <div className="sidebar_filter">
              <label htmlFor="filter_name">{t('Municipality')}</label>
              <Dropdown
                id="filter_municipality"
                value={opstinaId}
                onChange={(e) => setOpstinaId(e.value)}
                disabled={isMunicipalitiesLoading}
                options={municipalityFilterOptions}
                filter
                filterPlaceholder={t('Search')}
                showClear
              />
            </div>
          )}
          {placesFilterGuard && (
            <div className="sidebar_filter">
              <label htmlFor="filter_name">{t('Place')}</label>
              <Dropdown
                id="filter_place"
                value={mestoId}
                onChange={(e) => setMestoId(e.value)}
                disabled={isPlacesLoading}
                options={placeFilterOptions}
                filter
                filterPlaceholder={t('Search')}
                showClear
              />
            </div>
          )}
        </AccordionTab>
      </Accordion>
    ),
    [
      nameFilter,
      mestoId,
      opstinaId,
      placeFilterOptions,
      municipalityFilterOptions,
      basicFiltersActiveFilterCount,
      isMunicipalitiesLoading,
      isPlacesLoading,
      municipalityFilterGuard,
      placesFilterGuard,
      t,
    ]
  );

  const headerFiltersCount = useMemo(
    () => basicFiltersActiveFilterCount,
    [basicFiltersActiveFilterCount]
  );

  function resetAllFilters() {
    setNameFilter('');
    setMestoId('');
    setOpstinaId('');
  }

  return {
    filters,
    resetAllFilters,
    httpFiltersObj,
    headerFiltersCount,
  };
}

export default useTableFilters;
